import axios from 'axios';
import { camelToSnakeCase, keysToCamelCase } from '@/utils/caseConverter';
import { resizeImage } from '@/utils/imageResize';
import {
  MenuExecutionFormData,
  MenuExecutionRequestParams,
  MenuExecutionResponseParams,
} from '@/types';

// APIエンドポイントのベースURL
const API_BASE = `${import.meta.env.VITE_AISHA_API_BASE || 'http://localhost:7999/api'}/menus`;

export const convertFormDataToRequestParams = (formData: MenuExecutionFormData): MenuExecutionRequestParams => {
  // 高度編集エンジンで編集画像がない場合、参照画像1枚目を編集画像として使用
  const isAdvancedEditEngine = ['black_forest_labs2_pro', 'black_forest_labs2_flex', 'nanobanana', 'nanobanana_pro', 'nanobanana2', 'grok_imagen', 'seedream4', 'gpt_image'].includes(formData.menu?.engine || '');
  const useEditImage = formData.menu?.useEditImage || formData.menu?.use_edit_image;
  const refImages = formData.referenceImages || [];

  let editImage = formData.editImage ?? undefined;
  // 高度編集エンジンで、編集画像設定がなく、参照画像がある場合は1枚目を編集画像としても送信
  if (isAdvancedEditEngine && !useEditImage && refImages.length > 0 && refImages[0]) {
    editImage = refImages[0];
  }

  return {
    menuId: formData.menu!.id,
    image: formData.image ?? undefined,
    additionalPromptForMyCar: formData.additionalPromptForMyCar || undefined,
    additionalPromptForOthers: formData.additionalPromptForOthers || undefined,
    aspectRatio: formData.aspectRatio || undefined,
    promptVariables: formData.promptVariables?.length
      ? formData.promptVariables
      : undefined,
    // FLUX.2用
    editImage: editImage,
    // 動画i2v用: エンドフレーム
    endImage: formData.endImage ?? undefined,
    // HeyGen用
    script: formData.script || undefined,
    audioFile: formData.audioFile ?? undefined,
    referenceImages: formData.referenceImages?.length
      ? formData.referenceImages
      : undefined,
    // 愛車ID（愛車テキスト参照用）
    carId: formData.carId || undefined,
  };
}

export const executeMenu = async (
  params: MenuExecutionRequestParams,
  userId?: string,
  onError?: (error: unknown) => void,
): Promise<MenuExecutionResponseParams | null> => {
  const formData = new FormData();

  // 対象のparamsからmenuIdとimageを除外して処理（menuIdはURLに使うため・画像は別処理が必要なため）
  const { menuId, image, editImage, endImage, referenceImages, carId, script, audioFile, ...rest } = params as any;

  // user_idを追加
  if (userId) {
    formData.append('user_id', userId);
  }

  // frontend_idを生成して追加（Date.now()を使用）
  const frontendId = Date.now().toString();
  formData.append('frontend_id', frontendId);
  console.log(`🆔 Generated frontend_id: ${frontendId}`);

  // car_idを追加（愛車テキスト参照用）
  if (carId) {
    formData.append('car_id', carId);
    console.log(`🚗 Added car_id: ${carId}`);
  }

  // 画像リサイズヘルパー関数
  const processImage = async (img: File, fieldName: string) => {
    try {
      console.log(`🖼️ ${fieldName}画像サイズをチェック中: ${img.name}`);
      const resizedImage = await resizeImage(img, 2000, 1.0);
      formData.append(fieldName, resizedImage, resizedImage.name);
      console.log(`✅ ${fieldName}画像リサイズ完了: ${resizedImage.name}`);
    } catch (error) {
      console.error(`❌ ${fieldName}画像リサイズエラー:`, error);
      formData.append(fieldName, img, img.name);
      console.log(`⚠️ ${fieldName}元画像をそのまま使用: ${img.name}`);
    }
  };

  // 通常の画像がある場合はリサイズしてからFormDataに追加
  if (image instanceof File) {
    await processImage(image, 'image');
  }

  // FLUX.2用: 編集画像
  if (editImage instanceof File) {
    await processImage(editImage, 'edit_image');
  }

  // 動画i2v用: エンドフレーム画像
  if (endImage instanceof File) {
    await processImage(endImage, 'end_image');
  }

  // HeyGen: スクリプト・音声
  if (script) {
    formData.append('script', script);
  }
  if (audioFile instanceof File) {
    formData.append('audio', audioFile, audioFile.name);
  }

  // FLUX.2用: 参照画像（複数）
  if (referenceImages && referenceImages.length > 0) {
    for (let i = 0; i < referenceImages.length; i++) {
      const refImg = referenceImages[i];
      if (refImg instanceof File) {
        await processImage(refImg, 'reference_images');
      }
    }
  }

  // すべてのparamsのkeyをスネークケースに変換してFormDataに追加
  Object.entries(rest).forEach(([key, value]) => {
    if (value == null) return;

    const snakeKey = camelToSnakeCase(key);
    if (snakeKey === 'prompt_variables') {
      formData.append(snakeKey, JSON.stringify(value));
    } else {
      formData.append(snakeKey, value as any);
    }
  });

  try {
    const response = await axios.post(
      `${API_BASE}/${params.menuId}/execute/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    console.log('🔍 Raw API Response:', response.data); // デバッグログ追加
    const camelCaseData = keysToCamelCase(response.data);
    console.log('🔍 Camel Case Data:', camelCaseData); // デバッグログ追加
    
    // frontendIdをレスポンスに追加
    return {
      ...camelCaseData,
      frontendId: frontendId
    };
  } catch (err) {
    console.error('画像生成API実行失敗', err);
    onError?.(err);
    return null;
  }
};
